import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, BookOpen, Users, CreditCard, Video, BarChart3, FileEdit, Settings, Home, Menu, LogOut, TicketPercent } from "lucide-react";
import { Spinner } from "@/components/site/Bits";
import { useMe } from "@/lib/site";

const NAV = [
  ["", "الإحصائيات", LayoutDashboard, "dashboard"],
  ["courses", "إدارة الدورات", BookOpen, "courses"],
  ["students", "المتدربون", Users, "students"],
  ["payments", "المدفوعات", CreditCard, "payments"],
  ["coupons", "القسائم", TicketPercent, "coupons"],
  ["meetings", "الاجتماعات والحضور والشهادات", Video, "meetings"],
  ["reports", "التقارير", BarChart3, "reports"],
  ["content", "محتوى الموقع", FileEdit, "content"],
  ["system", "المستخدمون والسجلات", Settings, "system"],
];

export default function AdminLayout() {
  const { me, loading, signOut } = useMe();
  const [open, setOpen] = useState(false);
  if (loading) return <Spinner />;
  if (me?.role !== "admin")
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="admin-forbidden">
        <div><h1 className="font-display text-2xl font-bold">غير مصرح لك بالدخول إلى لوحة التحكم</h1><Link to="/" className="btn-copper mt-6">العودة للرئيسية</Link></div>
      </div>
    );
  return (
    <div className="min-h-screen bg-[var(--ivory)] lg:flex" data-testid="admin-layout">
      <aside className={`${open ? "block" : "hidden"} lg:block lg:w-72 shrink-0 bg-[#1F1815] text-white lg:min-h-screen lg:sticky lg:top-0`}>
        <div className="p-6 border-b border-white/10">
          <img src="/logo-transparent.png" alt="هِمّة تم" className="h-12 w-auto" />
          <p className="mt-3 font-display font-bold">لوحة التحكم</p>
        </div>
        <nav className="p-3 space-y-1" onClick={() => setOpen(false)}>
          {NAV.map(([to, label, Icon, id]) => (
            <NavLink key={id} to={`/admin${to ? `/${to}` : ""}`} end data-testid={`admin-nav-${id}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${isActive ? "bg-[var(--copper)] text-white" : "text-white/75 hover:bg-white/10"}`}>
              <Icon size={18} />{label}
            </NavLink>
          ))}
          <Link to="/" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-white/60 hover:bg-white/10" data-testid="admin-nav-site"><Home size={18} />عرض الموقع</Link>
        </nav>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="h-16 border-b border-[var(--line)] bg-white/80 backdrop-blur flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
          <button className="lg:hidden p-2 border rounded-lg" onClick={() => setOpen(!open)} data-testid="admin-menu-toggle"><Menu size={20} /></button>
          <p className="text-sm text-[var(--muted-ink)]">مرحباً، {me.name || me.email}</p>
          <button onClick={() => { signOut(); window.location.replace("/"); }} className="inline-flex items-center gap-1 text-sm text-[var(--muted-ink)] hover:text-red-600" data-testid="admin-logout-btn"><LogOut size={16} />خروج</button>
        </header>
        <main className="p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}

export const AdminTitle = ({ title, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
    <h1 className="font-display text-2xl md:text-3xl font-extrabold">{title}</h1>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Stat = ({ label, value, Icon, testId }) => (
  <div className="card-lux !transform-none p-5" data-testid={testId}>
    <div className="flex items-center justify-between"><p className="text-sm text-[var(--muted-ink)]">{label}</p>{Icon && <Icon size={18} className="text-[var(--copper)]" />}</div>
    <p className="font-display text-2xl md:text-3xl font-black mt-2">{value}</p>
  </div>
);

export const DataTable = ({ head, children, testId }) => (
  <div className="card-lux !transform-none overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="bg-[var(--paper)]"><tr>{head.map((h) => <th key={h} className="text-right p-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody className="[&>tr]:border-t [&>tr]:border-[var(--line)] [&_td]:p-3">{children}</tbody>
    </table>
  </div>
);
